import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES } from '@bre-ticketing/contracts';
import { Link } from 'react-router';
import type { ApiError, StaffRow } from '../api';
import { endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen, statusTone } from '../ui';

export default function StaffPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const staff = useQuery<{ staff: StaffRow[] }, ApiError>({ queryKey: ['staff'], queryFn: endpoints.staff });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['staff'] });
  const onError = (cause: ApiError) => setError(cause.message);

  const setStatus = useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason: string }) =>
      endpoints.setStatus(id, status, reason),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError,
  });

  if (!can('staff.view')) return <NotPermitted permission="staff.view" />;

  const rows = staff.data?.staff ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {can('staff.manage') ? <AddStaff onDone={() => void refresh()} onError={onError} /> : null}

      <Panel
        title="Staff"
        description="Who can sign in, what they may do, and whether they have a window PIN."
        actions={
          <Link
            to="/staff/pending"
            className="text-sm font-medium text-accent underline-offset-2 hover:underline"
          >
            Pending logins →
          </Link>
        }
      >
        <Table headers={['Name', 'Roles', 'Sign-in', 'Status', 'Added', '']}>
          {staff.isPending ? (
            <EmptyRow columns={6}>Loading…</EmptyRow>
          ) : rows.length === 0 ? (
            <EmptyRow columns={6}>
              No staff accounts yet. The first one is created with{' '}
              <code className="rounded bg-page px-1">scripts/bootstrap-admin.mjs</code>.
            </EmptyRow>
          ) : (
            rows.map((row) => (
              <StaffTableRow
                key={row.id}
                row={row}
                canManage={can('staff.manage')}
                canManageRoles={can('roles.manage')}
                isEditing={editing === row.id}
                onEdit={() => {
                  setEditing(editing === row.id ? null : row.id);
                  setError(null);
                }}
                onDone={() => {
                  setEditing(null);
                  void refresh();
                }}
                onError={onError}
                onStatus={(status) => {
                  const reason = window.prompt(`Reason for marking ${row.display_name} ${status}?`);
                  if (reason) setStatus.mutate({ id: row.id, status, reason });
                }}
              />
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function StaffTableRow({
  row,
  canManage,
  canManageRoles,
  isEditing,
  onEdit,
  onDone,
  onError,
  onStatus,
}: {
  row: StaffRow;
  canManage: boolean;
  canManageRoles: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onError: (error: ApiError) => void;
  onStatus: (status: string) => void;
}) {
  return (
    <>
      <tr className="border-b border-line last:border-0">
        <td className="px-5 py-3">
          <div className="font-medium text-ink-900">{row.display_name}</div>
          <div className="text-xs text-ink-400">{row.login_email ?? row.email_snapshot ?? 'No email on file'}</div>
        </td>
        <td className="px-5 py-3">
          {row.roles.length === 0 ? (
            <span className="text-xs text-ink-400">No roles</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.roles.map((role) => (
                <Badge key={role} tone="accent">
                  {role.replaceAll('_', ' ')}
                </Badge>
              ))}
            </div>
          )}
        </td>
        <td className="px-5 py-3 text-xs text-ink-600">
          {row.login_email ? (row.google_linked ? 'Email · Google' : 'Email') : null}
          {row.login_email && row.has_pin ? ' · ' : null}
          {row.has_pin ? 'PIN' : null}
          {!row.login_email && !row.has_pin ? <span className="text-ink-400">Cannot sign in</span> : null}
        </td>
        <td className="px-5 py-3">
          <Badge tone={statusTone(row.status)}>{row.status}</Badge>
        </td>
        <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(row.created_at)}</td>
        <td className="px-5 py-3 text-right whitespace-nowrap">
          {canManage || canManageRoles ? (
            <Button onClick={onEdit}>{isEditing ? 'Close' : 'Manage'}</Button>
          ) : null}
        </td>
      </tr>

      {isEditing ? (
        <tr className="border-b border-line bg-page/60">
          <td colSpan={6} className="px-5 py-4">
            <div className="flex flex-wrap items-start gap-8">
              {canManageRoles ? <RoleEditor row={row} onDone={onDone} onError={onError} /> : null}
              {canManage ? <LoginEmailEditor row={row} onDone={onDone} onError={onError} /> : null}
              {canManage ? <PinEditor row={row} onDone={onDone} onError={onError} /> : null}
              {canManage ? (
                <div>
                  <p className="mb-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">Status</p>
                  {row.status === 'ACTIVE' ? (
                    <Button variant="danger" onClick={() => onStatus('SUSPENDED')}>
                      Suspend
                    </Button>
                  ) : (
                    <Button onClick={() => onStatus('ACTIVE')}>Reactivate</Button>
                  )}
                  <p className="mt-2 max-w-48 text-xs text-ink-400">
                    Suspending signs them out of every device immediately.
                  </p>
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RoleEditor({
  row,
  onDone,
  onError,
}: {
  row: StaffRow;
  onDone: () => void;
  onError: (error: ApiError) => void;
}) {
  const [selected, setSelected] = useState<string[]>(row.roles);

  const save = useMutation({
    mutationFn: () => endpoints.setRoles(row.id, selected),
    onSuccess: onDone,
    onError,
  });

  return (
    <div>
      <p className="mb-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">Roles</p>
      <div className="space-y-1">
        {ROLES.map((role) => (
          <label key={role} className="flex items-center gap-2 text-sm text-ink-900">
            <input
              type="checkbox"
              checked={selected.includes(role)}
              onChange={(event) =>
                setSelected((current) =>
                  event.target.checked ? [...current, role] : current.filter((value) => value !== role),
                )
              }
            />
            {role.replaceAll('_', ' ')}
          </label>
        ))}
      </div>
      <Button variant="primary" className="mt-3" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save roles'}
      </Button>
    </div>
  );
}

/**
 * A new staff member (D-370): their name, the address they will sign in with
 * (Google or an emailed link), and roles. Leave the address empty for somebody
 * who only ever uses a PIN at the window or the gate.
 */
function AddStaff({ onDone, onError }: { onDone: () => void; onError: (error: ApiError) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>([]);

  const save = useMutation({
    mutationFn: () => endpoints.createStaff({ display_name: name.trim(), login_email: email.trim() || null, roles }),
    onSuccess: () => {
      setName('');
      setEmail('');
      setRoles([]);
      setOpen(false);
      onDone();
    },
    onError,
  });

  if (!open) {
    return (
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setOpen(true)}>
          Add staff member
        </Button>
      </div>
    );
  }

  return (
    <Panel title="Add staff member" description="They sign in with Google or an emailed link to this address. Leave it empty for PIN-only window or gate staff.">
      <div className="flex flex-wrap items-start gap-6 px-5 py-4">
        <div className="space-y-2">
          <label className="block text-sm text-ink-900">
            Name
            <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 block w-64 rounded border border-line bg-surface px-2 py-1.5 text-sm" />
          </label>
          <label className="block text-sm text-ink-900">
            Sign-in email
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" className="mt-1 block w-64 rounded border border-line bg-surface px-2 py-1.5 text-sm" />
          </label>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">Roles</p>
          <div className="space-y-1">
            {ROLES.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm text-ink-900">
                <input
                  type="checkbox"
                  checked={roles.includes(role)}
                  onChange={(event) => setRoles((current) => (event.target.checked ? [...current, role] : current.filter((value) => value !== role)))}
                />
                {role.replaceAll('_', ' ')}
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2 self-end">
          <Button variant="primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Add'}
          </Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      </div>
    </Panel>
  );
}

/** Changing the address unlinks Google and signs them out everywhere (D-370). */
function LoginEmailEditor({
  row,
  onDone,
  onError,
}: {
  row: StaffRow;
  onDone: () => void;
  onError: (error: ApiError) => void;
}) {
  const [email, setEmail] = useState(row.login_email ?? '');

  const save = useMutation({
    mutationFn: () => endpoints.updateStaff(row.id, { login_email: email.trim() || null }),
    onSuccess: onDone,
    onError,
  });

  return (
    <div>
      <p className="mb-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">Sign-in email</p>
      <input
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="name@example.com"
        className="w-56 rounded border border-line bg-surface px-2 py-1.5 text-sm"
      />
      <Button
        variant="primary"
        className="mt-3 block"
        disabled={save.isPending || email.trim().toLowerCase() === (row.login_email ?? '')}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : 'Save email'}
      </Button>
      <p className="mt-2 max-w-56 text-xs text-ink-400">
        {row.google_linked ? 'Linked to a Google account. ' : ''}Changing it signs them out and unlinks Google. Empty means PIN only.
      </p>
    </div>
  );
}

function PinEditor({
  row,
  onDone,
  onError,
}: {
  row: StaffRow;
  onDone: () => void;
  onError: (error: ApiError) => void;
}) {
  const [pin, setPin] = useState('');

  const save = useMutation({
    mutationFn: () => endpoints.setPin(row.id, pin),
    onSuccess: () => {
      setPin('');
      onDone();
    },
    onError,
  });

  return (
    <div>
      <p className="mb-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">
        {row.has_pin ? 'Replace PIN' : 'Set PIN'}
      </p>
      <input
        id={`pin-${row.id}`}
        value={pin}
        onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="off"
        placeholder="6 digits"
        className="tabular w-32 rounded border border-line bg-surface px-2 py-1.5 text-sm"
      />
      <Button
        variant="primary"
        className="mt-3 block"
        disabled={pin.length < 6 || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : 'Save PIN'}
      </Button>
      <p className="mt-2 max-w-48 text-xs text-ink-400">
        PINs are unique across the organization. No repeated or sequential digits.
      </p>
    </div>
  );
}
